import { Option, Value } from 'baseui/select';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  HotelSaleAddOnsItemFormType,
  HotelSaleFormType,
  HotelSaleHotelItemFormType,
} from '../form-type';
import { addNewAddOnsItem } from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
}

function useGetScheduleAddOnsTableList({ parentIndex = 0 }) {
  const { t } = useTranslation();
  const guestName = `hotelSales.${parentIndex}.hotelSaleGuests` as const;

  const { control } = useFormContext<HotelSaleFormType>();

  const guests = useWatch({
    control,
    name: guestName,
  });
  const { editable } = useFormState();

  const columns = React.useMemo<IColumn<HotelSaleHotelItemFormType>[]>(() => {
    const guestList: Option[] = guests
      .filter((guest) => !!guest.guestId)
      .map((guest) => {
        return {
          id: guest.guestId,
          label: guest.guestName,
          extra: guest,
        };
      });

    return [
      {
        header: t('common:name'),
        accessorKey: 'guestId',
        minSize: 100,
        dataType: ColumnDataType.select,
        options: guestList,
        required: true,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 500,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:price_@_unit'),
        accessorKey: 'priceUnit',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
      },
      {
        header: t('common:qty'),
        accessorKey: 'unit',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
      },
      {
        header: t('common:total_price'),
        accessorKey: 'price',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        disabled: true,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [editable, guests, t]);

  return columns;
}

export default function ScheduleAddOnsTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const name = `hotelSales.${props.parentIndex}.hotelSaleAddOns` as const;
  const { clearErrors, getValues, trigger } = useFormContext();

  const { control } = useFormContext<HotelSaleFormType>();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetScheduleAddOnsTableList({
    parentIndex: props.parentIndex,
  });

  const dispatch = React.useCallback(
    (action) => {
      const addons = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const addon = addons[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof HotelSaleAddOnsItemFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<HotelSaleAddOnsItemFormType> = () => {
        if (columnId === 'guestId') {
          return { guestName: extra?.[0]?.label as string };
        }
        if (columnId === 'priceUnit') {
          return { price: (value || 0) * (addon.unit || 0) };
        }
        if (columnId === 'unit') {
          return { price: (value || 0) * (addon.priceUnit || 0) };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewAddOnsItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...addon,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  if (fields.length || editable) {
    return (
      <>
        <Text variant="ParagraphMedium">{t('ticketSales:add_ons')}</Text>
        <Separator gap={12} />
        <div style={{ width: '100%' }}>
          <TableInput
            columns={columns}
            data={fields}
            dispatch={dispatch}
            disabled={!editable}
            name={name}
            uniqueRowKey="customId"
          />
        </div>
      </>
    );
  }

  return null;
}
